import { genPageTheme } from '@backstage/theme';
import { alpha, darken, lighten, Theme } from '@mui/material/styles';

export type ThemeVariant = 'light' | 'dark';

export const fjordBrand = {
  aqua: {
    50: '#E0F1F2',
    100: '#B0DCDE',
    200: '#7DC6C8',
    300: '#48B0B1',
    400: '#1D9F9F',
    500: '#008E8C',
    600: '#00827F',
    700: '#00726E',
    800: '#00625E',
    900: '#004741',
  },
  magenta: {
    50: '#F7E6F3',
    100: '#EBC0E2',
    200: '#DE96CF',
    300: '#CF6BBC',
    400: '#C349AE',
    500: '#B627A1',
    600: '#A9229B',
    700: '#971B94',
    800: '#87168C',
    900: '#6B0D7F',
  },
};

type ModeTokens = {
  background: string;
  paper: string;
  surfaceSubtle: string;
  surfaceRaised: string;
  surfaceAccent: string;
  surfaceCanvas: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textContrast: string;
  border: string;
  borderStrong: string;
  shadow: string;
  shadowSoft: string;
  navigationBackground: string;
  navigationSubmenu: string;
  navigationHover: string;
  navigationColor: string;
  navigationSelected: string;
  navigationIndicator: string;
  navigationBorder: string;
  primaryMain: string;
  primaryLight: string;
  primaryDark: string;
  secondaryMain: string;
  secondaryLight: string;
  secondaryDark: string;
  pageStart: string;
  pageEnd: string;
  pageHighlight: string;
  pageGlow: string;
  focusRing: string;
};

const lightTokens: ModeTokens = {
  background: '#F7F9FA',
  paper: '#FFFFFF',
  surfaceSubtle: '#F4F7F8',
  surfaceRaised: '#FFFFFF',
  surfaceAccent: alpha(fjordBrand.aqua[100], 0.28),
  surfaceCanvas: '#FBFCFD',
  textPrimary: '#11222B',
  textSecondary: '#3E5560',
  textMuted: '#6E8591',
  textContrast: '#11222B',
  border: alpha('#11222B', 0.12),
  borderStrong: alpha('#11222B', 0.18),
  shadow: '0 20px 48px rgba(17, 34, 43, 0.08)',
  shadowSoft: '0 8px 24px rgba(17, 34, 43, 0.05)',
  navigationBackground: '#F8FAFB',
  navigationSubmenu: '#F3F6F8',
  navigationHover: alpha(fjordBrand.aqua[200], 0.22),
  navigationColor: '#435761',
  navigationSelected: '#11222B',
  navigationIndicator: fjordBrand.aqua[400],
  navigationBorder: alpha('#11222B', 0.08),
  primaryMain: fjordBrand.aqua[600],
  primaryLight: fjordBrand.aqua[50],
  primaryDark: fjordBrand.aqua[700],
  secondaryMain: fjordBrand.magenta[500],
  secondaryLight: fjordBrand.magenta[50],
  secondaryDark: fjordBrand.magenta[700],
  pageStart: '#F9FCFC',
  pageEnd: '#EEF6F7',
  pageHighlight: alpha(fjordBrand.aqua[100], 0.52),
  pageGlow: alpha(fjordBrand.magenta[100], 0.18),
  focusRing: alpha(fjordBrand.aqua[300], 0.34),
};

const darkTokens: ModeTokens = {
  background: '#081116',
  paper: '#101A20',
  surfaceSubtle: '#0D161C',
  surfaceRaised: '#13212A',
  surfaceAccent: alpha(fjordBrand.magenta[500], 0.18),
  surfaceCanvas: '#0A1217',
  textPrimary: '#E8F1F3',
  textSecondary: '#BED0D6',
  textMuted: '#8EA6AF',
  textContrast: '#F7FCFD',
  border: alpha('#DCE8EB', 0.12),
  borderStrong: alpha('#DCE8EB', 0.2),
  shadow: '0 24px 64px rgba(0, 0, 0, 0.42)',
  shadowSoft: '0 12px 34px rgba(0, 0, 0, 0.3)',
  navigationBackground: '#061015',
  navigationSubmenu: '#0B1921',
  navigationHover: alpha('#FFFFFF', 0.06),
  navigationColor: alpha('#FFFFFF', 0.7),
  navigationSelected: '#FFFFFF',
  navigationIndicator: fjordBrand.aqua[400],
  navigationBorder: alpha('#DCE8EB', 0.1),
  primaryMain: fjordBrand.aqua[400],
  primaryLight: alpha(fjordBrand.aqua[200], 0.18),
  primaryDark: fjordBrand.aqua[600],
  secondaryMain: fjordBrand.magenta[400],
  secondaryLight: alpha(fjordBrand.magenta[200], 0.2),
  secondaryDark: fjordBrand.magenta[600],
  pageStart: '#09353A',
  pageEnd: '#56215F',
  pageHighlight: alpha('#FFFFFF', 0.08),
  pageGlow: alpha(fjordBrand.magenta[300], 0.2),
  focusRing: alpha(fjordBrand.aqua[300], 0.32),
};

export const getModeTokens = (variant: ThemeVariant) =>
  variant === 'dark' ? darkTokens : lightTokens;

export const getThemeTokens = (theme: Theme) =>
  getModeTokens(theme.palette.mode === 'dark' ? 'dark' : 'light');

export const shellMetrics = {
  railClosedWidth: 72,
  railOpenWidth: 224,
  mobileSidebarHeight: 56,
  railClosedInset: 8,
  railOpenInset: 16,
  railLogoTop: 20,
  railLogoBottom: 14,
  railRowHeight: 44,
  railSearchHeight: 40,
  railItemPaddingX: 14,
  railIconSlot: 34,
  railItemGap: 10,
  railDividerGap: 10,
  contentMaxWidth: 1180,
  docsMaxWidth: 1400,
  sectionGap: 24,
  cardPadding: 28,
  compactPadding: 24,
} as const;

export const radii = {
  sm: 8,
  md: 10,
  lg: 12,
  xl: 14,
} as const;

export const pageContentWidth = shellMetrics.contentMaxWidth;

// TechDocs needs two 16rem sidebars beside the article, so it gets a wider
// column than other pages to keep the article itself at a readable width.
export const docsPageWidth = shellMetrics.docsMaxWidth;

export const getSurfaceColor = (
  theme: Theme,
  level: 'base' | 'subtle' | 'raised' | 'accent' = 'base',
) => {
  const tokens = getThemeTokens(theme);

  switch (level) {
    case 'base':
      return tokens.surfaceCanvas;
    case 'subtle':
      return tokens.surfaceSubtle;
    case 'raised':
      return tokens.surfaceRaised;
    case 'accent':
      return tokens.surfaceAccent;
    default:
      return tokens.paper;
  }
};

export const getSurfaceBorder = (
  theme: Theme,
  emphasis: 'subtle' | 'strong' = 'subtle',
) => {
  const tokens = getThemeTokens(theme);
  return emphasis === 'strong' ? tokens.borderStrong : tokens.border;
};

export const getFocusRing = (theme: Theme) =>
  `0 0 0 4px ${getThemeTokens(theme).focusRing}`;

export const getPageThemes = (variant: ThemeVariant) => {
  const tokens = getModeTokens(variant);

  const createShape = (
    start: string,
    end: string,
    glow: string,
    intensity: number,
  ) =>
    [
      `radial-gradient(circle at 14% 18%, ${alpha(
        start,
        intensity,
      )} 0, transparent 28%)`,
      `radial-gradient(circle at 85% 12%, ${alpha(
        end,
        intensity * 0.9,
      )} 0, transparent 30%)`,
      `radial-gradient(circle at 56% 0%, ${glow} 0, transparent 34%)`,
      `linear-gradient(140deg, ${tokens.pageHighlight} 0%, transparent 44%)`,
    ].join(', ');

  return {
    home: genPageTheme({
      colors: [tokens.pageStart, tokens.pageEnd],
      options: { fontColor: tokens.textPrimary },
      shape: createShape(
        tokens.primaryMain,
        tokens.secondaryMain,
        tokens.pageGlow,
        0.14,
      ),
    }),
    tool: genPageTheme({
      colors: [darken(tokens.pageStart, 0.08), tokens.pageEnd],
      options: {
        fontColor:
          variant === 'dark' ? tokens.textContrast : tokens.textPrimary,
      },
      shape: createShape(
        tokens.secondaryMain,
        tokens.primaryMain,
        tokens.pageGlow,
        0.12,
      ),
    }),
    apis: genPageTheme({
      colors: [tokens.primaryMain, darken(tokens.pageStart, 0.16)],
      options: {
        fontColor:
          variant === 'dark' ? tokens.textContrast : tokens.textPrimary,
      },
      shape: createShape(
        tokens.primaryMain,
        lighten(tokens.primaryMain, 0.22),
        tokens.pageGlow,
        0.14,
      ),
    }),
    documentation: genPageTheme({
      colors: [tokens.pageEnd, darken(tokens.pageStart, 0.06)],
      options: { fontColor: tokens.textPrimary },
      shape: createShape(
        tokens.secondaryMain,
        tokens.primaryMain,
        tokens.pageGlow,
        0.12,
      ),
    }),
    service: genPageTheme({
      colors: [darken(tokens.pageStart, 0.04), darken(tokens.pageEnd, 0.08)],
      options: {
        fontColor:
          variant === 'dark' ? tokens.textContrast : tokens.textPrimary,
      },
      shape: createShape(
        tokens.primaryDark,
        tokens.secondaryDark,
        tokens.pageGlow,
        0.12,
      ),
    }),
    website: genPageTheme({
      colors: [darken(tokens.primaryMain, 0.06), tokens.pageStart],
      options: {
        fontColor:
          variant === 'dark' ? tokens.textContrast : tokens.textPrimary,
      },
      shape: createShape(
        tokens.primaryMain,
        tokens.secondaryLight,
        tokens.pageGlow,
        0.12,
      ),
    }),
    library: genPageTheme({
      colors: [tokens.pageEnd, darken(tokens.pageEnd, 0.08)],
      options: { fontColor: tokens.textPrimary },
      shape: createShape(
        tokens.secondaryMain,
        tokens.primaryLight,
        tokens.pageGlow,
        0.1,
      ),
    }),
    other: genPageTheme({
      colors: [tokens.pageStart, darken(tokens.pageStart, 0.16)],
      options: {
        fontColor:
          variant === 'dark' ? tokens.textContrast : tokens.textPrimary,
      },
      shape: createShape(
        tokens.primaryLight,
        tokens.secondaryLight,
        tokens.pageGlow,
        0.1,
      ),
    }),
    app: genPageTheme({
      colors: [tokens.pageStart, tokens.primaryMain],
      options: {
        fontColor:
          variant === 'dark' ? tokens.textContrast : tokens.textPrimary,
      },
      shape: createShape(
        tokens.primaryMain,
        tokens.secondaryLight,
        tokens.pageGlow,
        0.12,
      ),
    }),
    card: genPageTheme({
      colors: [tokens.primaryMain, tokens.secondaryMain],
      options: {
        fontColor:
          variant === 'dark' ? tokens.textContrast : tokens.textPrimary,
      },
      shape: createShape(
        tokens.primaryLight,
        tokens.secondaryLight,
        tokens.pageGlow,
        0.14,
      ),
    }),
  };
};

export const meltwaterTypography = {
  htmlFontSize: 16,
  fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
  h1: {
    fontSize: '3.25rem',
    fontWeight: 700,
    marginBottom: 16,
  },
  h2: {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: 16,
  },
  h3: {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: 14,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: 12,
  },
  h5: {
    fontSize: '1.5rem',
    fontWeight: 400,
    marginBottom: 8,
  },
  h6: {
    fontSize: '1.25rem',
    fontWeight: 400,
    marginBottom: 8,
  },
};
